import React, { useEffect, useMemo, useState, createElement, Component } from 'react';
import { Navbar } from '../Navbar';
import { Footer } from '../Footer';
import { TeacherSidebar } from '../TeacherSidebar';
import { AddAlertModal } from '../AddAlertModal';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import { MenuIcon, XIcon, AlertCircleIcon, CalendarIcon, TrendingUpIcon, UserIcon, FileTextIcon, ChevronRightIcon, LoaderIcon, SearchIcon, ArrowUpIcon, ArrowDownIcon, ChevronsUpDownIcon, PlusIcon, TrendingDownIcon, MinusIcon, DownloadIcon, CheckCircleIcon, XCircleIcon, ClipboardListIcon, UserXIcon } from 'lucide-react';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { useTheme } from '../providers/ThemeProvider';
import { useLessons } from '../../src/hooks/useLessons';
// Import services
import { createAlert, Alert, getAllAlerts, dismissAlert } from '../../services/alertService';
import { getTeacherStudents } from '../../services/studentService';
import { getPracticeHoursTrend, getStudentPracticeLogs } from '../../services/practiceLogService';
import { getTeacherTasks } from '../../services/taskService';
import { StudentAlertPanel } from '../alerts/StudentAlertPanel';
import { AddStudentModal } from '../modals/AddStudentModal';
import { StudentProgressPanel } from '../students/StudentProgressPanel';
import { calculateReportsMetrics, filterLessonsByDateRange, filterTasksByDateRange } from '../../services/reportsMetricsService';
// Skeleton loader component
const SkeletonLoader = ({
  className = '',
  darkMode
}) => {
  return <div className={`animate-pulse rounded ${className} ${darkMode ? 'bg-gradient-to-r from-gray-700 via-gray-600 to-gray-700' : 'bg-gradient-to-r from-gray-300 via-gray-200 to-gray-300'}`} style={{
    backgroundSize: '200% 100%',
    animation: 'shimmer 1.5s infinite'
  }} data-id="element-5782">
      <style jsx data-id="element-5783">{`
        @keyframes shimmer {
          0% {
            background-position: -200% 0;
          }
          100% {
            background-position: 200% 0;
          }
        }
      `}</style>
    </div>;
};
// Stat card component with loading and click states
const StatCard = ({
  icon,
  label,
  value,
  darkMode,
  onClick,
  isLoading
}) => {
  if (isLoading) {
    return <div className={`p-6 rounded-xl ${darkMode ? 'bg-navy-800' : 'bg-white'} shadow-sm`} data-id="element-5784">
        <div className="flex justify-between items-start" data-id="element-5785">
          <div className="flex-1" data-id="element-5786">
            <SkeletonLoader className="h-4 w-24 mb-2" darkMode={darkMode} data-id="element-5787" />
            <SkeletonLoader className="h-8 w-16" darkMode={darkMode} data-id="element-5788" />
          </div>
          <SkeletonLoader className="h-12 w-12 rounded-full" darkMode={darkMode} data-id="element-5789" />
        </div>
      </div>;
  }
  return <div className={`p-6 rounded-xl ${darkMode ? 'bg-navy-800' : 'bg-white'} shadow-sm cursor-pointer transition-all duration-200 hover:shadow-md hover:-translate-y-1`} onClick={onClick} data-id="element-5790">
      <div className="flex justify-between items-start" data-id="element-5791">
        <div data-id="element-5792">
          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-5793">
            {label}
          </p>
          <h3 className={`text-2xl font-bold mt-1 ${darkMode ? 'text-white' : 'text-gray-800'}`} data-id="element-5794">
            {value}
          </h3>
        </div>
        <div className={`p-3 rounded-full ${darkMode ? 'bg-navy-700' : 'bg-gray-100'}`} data-id="element-5795">
          {icon}
        </div>
      </div>
    </div>;
};
// Custom tooltip for charts
const CustomTooltip = ({
  active,
  payload,
  label,
  darkMode
}) => {
  if (active && payload && payload.length) {
    return <div className={`p-3 rounded-lg shadow-md ${darkMode ? 'bg-navy-700 text-white' : 'bg-white text-gray-800'} border ${darkMode ? 'border-navy-600' : 'border-gray-200'}`} data-id="element-5796">
        <p className="font-medium" data-id="element-5797">{label}</p>
        {payload.map((entry, index) => <p key={index} style={{
        color: entry.color
      }} data-id="element-5798">
            {entry.name}: {entry.value} hrs
          </p>)}
      </div>;
  }
  return null;
};
// Attendance Detail Modal Component
const AttendanceDetailModal = ({
  isOpen,
  onClose,
  type,
  darkMode
}) => {
  if (!isOpen) return null;
  const getModalContent = () => {
    switch (type) {
      case 'attendance':
        return {
          title: 'Attendance Rate Details',
          items: [{
            student: 'Emma Johnson',
            rate: '95%',
            status: 'Excellent'
          }, {
            student: 'Michael Chen',
            rate: '90%',
            status: 'Good'
          }, {
            student: 'Olivia Martinez',
            rate: '92%',
            status: 'Good'
          }, {
            student: 'Sophia Williams',
            rate: '75%',
            status: 'Needs Improvement'
          }, {
            student: 'Aiden Rodriguez',
            rate: '70%',
            status: 'Needs Improvement'
          }]
        };
      case 'cancellations':
        return {
          title: 'Recent Cancellations',
          items: [{
            student: 'Sophia Williams',
            date: 'Jan 10, 2024',
            reason: 'Illness'
          }, {
            student: 'Aiden Rodriguez',
            date: 'Jan 8, 2024',
            reason: 'Family emergency'
          }, {
            student: 'Michael Chen',
            date: 'Jan 5, 2024',
            reason: 'Schedule conflict'
          }, {
            student: 'Emma Johnson',
            date: 'Dec 28, 2023',
            reason: 'Holiday'
          }, {
            student: 'Olivia Martinez',
            date: 'Dec 20, 2023',
            reason: 'Weather'
          }]
        };
      case 'noshows':
        return {
          title: 'No-Show Incidents',
          items: [{
            student: 'Benjamin Taylor',
            date: 'Jan 12, 2024',
            status: 'Contacted'
          }, {
            student: 'Sophia Williams',
            date: 'Jan 6, 2024',
            status: 'Resolved'
          }]
        };
      default:
        return {
          title: '',
          items: []
        };
    }
  };
  const content = getModalContent();
  return <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-id="element-5799">
      <div className={`${darkMode ? 'bg-navy-800' : 'bg-white'} rounded-xl p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto`} data-id="element-5800">
        <div className="flex justify-between items-center mb-6" data-id="element-5801">
          <h3 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`} data-id="element-5802">
            {content.title}
          </h3>
          <button onClick={onClose} className={`${darkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'} transition-colors`} data-id="element-5803">
            <XIcon className="h-5 w-5" data-id="element-5804" />
          </button>
        </div>

        <div className="space-y-3" data-id="element-5805">
          {content.items.map((item, index) => <div key={index} className={`p-4 rounded-lg ${darkMode ? 'bg-navy-700' : 'bg-gray-50'}`} data-id="element-5806">
              <div className="flex justify-between items-center" data-id="element-5807">
                <div data-id="element-5808">
                  <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-5809">
                    {item.student}
                  </p>
                  {item.date && <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-5810">
                      {item.date}
                    </p>}
                </div>
                <div data-id="element-5811">
                  {item.rate && <span className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-5812">
                      {item.rate}
                    </span>}
                  {item.status && <span className={`text-xs px-2 py-1 rounded-full ${item.status === 'Excellent' || item.status === 'Resolved' ? darkMode ? 'bg-green-500/20 text-green-500' : 'bg-green-100 text-green-800' : item.status === 'Good' ? darkMode ? 'bg-blue-900/30 text-blue-200' : 'bg-blue-100 text-blue-800' : darkMode ? 'bg-yellow-900/30 text-yellow-200' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-5813">
                      {item.status}
                    </span>}
                  {item.reason && <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-5814">
                      {item.reason}
                    </span>}
                </div>
              </div>
            </div>)}
        </div>
      </div>
    </div>;
};
export const ReportsDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    theme
  } = useTheme();
  const {
    currentUser
  } = useAuth();
  const isDark = theme === 'dark';
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [selectedModal, setSelectedModal] = useState(null);
  const [isAddAlertModalOpen, setIsAddAlertModalOpen] = useState(false);
  // FEATURE FLAGS - Set to false to hide incomplete sections
  const SHOW_PRACTICE_CHART = false; // Practice hours trend chart
  const SHOW_KPI_CARDS = true; // Attendance rate & cancellations cards - NOW ENABLED WITH REAL DATA
  const SHOW_LESSON_NOTES = false; // Recent lesson notes section
  const SHOW_PROGRESS_COLUMNS = true; // Progress trend, practice level, attendance columns in table - ENABLED FOR P0 FIX
  // Loading states per section
  const [alertsLoading, setAlertsLoading] = useState(true);
  const [kpisLoading, setKpisLoading] = useState(true);
  const [practiceChartLoading, setPracticeChartLoading] = useState(true);
  const [studentsLoading, setStudentsLoading] = useState(true);
  const [notesLoading, setNotesLoading] = useState(true);
  // Real data states
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [students, setStudents] = useState<any[]>([]);
  const [practiceData, setPracticeData] = useState<any[]>([]);
  const [kpiMetrics, setKpiMetrics] = useState({
    activeStudents: 0,
    lessonCompletionRate: 0,
    noShowRate: 0,
    noShowCount: 0,
    cancellationRate: 0,
    taskCompletionRate: 0,
    totalLessons: 0,
    completedLessons: 0,
    noShowLessons: 0,
    cancelledLessons: 0,
    pastCancelledLessons: 0,
    upcomingCancelledLessons: 0,
    completedTasks: 0,
    totalTasks: 0
  });
  const [lessonNotes, setLessonNotes] = useState<any[]>([]);
  // Date range state - UPDATED: Always default to 'activenow' on page load (ignore localStorage)
  const [dateRangePreset, setDateRangePreset] = useState<string>('activenow');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  // REMOVED: No longer persist date range preset to localStorage
  // Always reset to 'activenow' on page load for predictable UX
  // Student filter state
  const [selectedStudentFilter, setSelectedStudentFilter] = useState<string>('all');
  // NEW: Read student query parameter from URL on mount
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const studentIdFromUrl = searchParams.get('student');
    if (studentIdFromUrl) {
      console.log('[REPORTS] Setting student filter from URL:', studentIdFromUrl);
      setSelectedStudentFilter(studentIdFromUrl);
      // Automatically switch to active alerts view when coming from dashboard
      setAlertFilter('active');
    }
  }, [location.search]);
  // Chart view state
  const [chartView, setChartView] = useState('week');
  const [showComparison, setShowComparison] = useState(false);
  // Lesson notes state
  const [notesSearchQuery, setNotesSearchQuery] = useState('');
  const [notesFilterStudent, setNotesFilterStudent] = useState('all');
  const [notesFilterInstrument, setNotesFilterInstrument] = useState('all');
  const [notesDisplayCount, setNotesDisplayCount] = useState(3);
  // Table state
  const [searchQuery, setSearchQuery] = useState('');
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [hoveredRow, setHoveredRow] = useState(null);
  // Alert filter state
  const [alertFilter, setAlertFilter] = useState<'active' | 'dismissed'>('active');
  // Enhanced filtering state
  const [quickFilter, setQuickFilter] = useState<'all' | 'at-risk' | 'high-performers'>('all');
  // Summary metrics state
  const [summaryMetrics, setSummaryMetrics] = useState({
    improving: 0,
    needsAttention: 0,
    highPerformers: 0,
    averageAttendance: 0
  });
  // Calculate date range based on preset - UPDATED: Added 'activenow' case
  const dateRange = useMemo(() => {
    const now = new Date();
    let startDate: Date;
    let endDate = new Date(now);
    switch (dateRangePreset) {
      case 'last7days':
        startDate = new Date(now);
        startDate.setDate(now.getDate() - 7);
        break;
      case 'last30days':
        startDate = new Date(now);
        startDate.setDate(now.getDate() - 30);
        break;
      case 'last90days':
        startDate = new Date(now);
        startDate.setDate(now.getDate() - 90);
        break;
      case 'activenow':
        // ADDED: Active Now = last 30 days + next 365 days (future lessons)
        startDate = new Date(now);
        startDate.setDate(now.getDate() - 30);
        endDate = new Date(now);
        endDate.setDate(now.getDate() + 365);
        break;
      case 'custom':
        if (customStartDate && customEndDate) {
          startDate = new Date(customStartDate);
          endDate = new Date(customEndDate);
        } else {
          startDate = new Date(now);
          startDate.setDate(now.getDate() - 90); // Changed from 30 to 90
        }
        break;
      default:
        startDate = new Date(now);
        startDate.setDate(now.getDate() - 90);
      // Changed from 30 to 90
    }
    return {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0],
      fromISOUtc: startDate.toISOString(),
      toISOUtc: endDate.toISOString()
    };
  }, [dateRangePreset, customStartDate, customEndDate]);
  // Fetch lessons using useLessons hook
  const {
    lessons
  } = useLessons({
    teacherId: currentUser?.uid || '',
    tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
    range: {
      fromISOUtc: dateRange.fromISOUtc,
      toISOUtc: dateRange.toISOUtc
    }
  });
  // Calculate KPIs from lessons and tasks - REAL DATA
  useEffect(() => {
    const calculateRealKPIs = async () => {
      if (!currentUser?.uid) {
        setKpisLoading(false);
        return;
      }
      try {
        setKpisLoading(true);
        console.log('═══════════════════════════════════════════════════════');
        console.log('[REPORTS DEBUG] Starting KPI calculation');
        console.log('[REPORTS DEBUG] Current filters:', {
          dateRangePreset,
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          selectedStudentFilter,
          selectedStudentFilterType: typeof selectedStudentFilter
        });
        console.log('[REPORTS DEBUG] Raw lessons count:', lessons.length);
        console.log('[REPORTS DEBUG] Sample lessons (first 3):', lessons.slice(0, 3).map(l => ({
          id: l.id,
          studentId: l.studentId,
          studentName: l.studentName,
          status: l.status,
          startAt: l.startAt
        })));
        // Filter lessons by date range
        const filteredLessons = filterLessonsByDateRange(lessons, dateRange.startDate, dateRange.endDate);
        console.log('[REPORTS DEBUG] After date filtering:', {
          original: lessons.length,
          filtered: filteredLessons.length
        });
        // Apply student filter to lessons if a specific student is selected
        const studentFilteredLessons = selectedStudentFilter === 'all' ? filteredLessons : filteredLessons.filter(lesson => {
          const matches = lesson.studentId === selectedStudentFilter;
          if (!matches) {
            console.log('[REPORTS DEBUG] Lesson filtered out:', {
              lessonStudentId: lesson.studentId,
              selectedStudentFilter,
              match: matches
            });
          }
          return matches;
        });
        console.log('[REPORTS DEBUG] After student filtering lessons:', {
          beforeFilter: filteredLessons.length,
          afterFilter: studentFilteredLessons.length,
          selectedStudent: selectedStudentFilter,
          uniqueStudentIds: [...new Set(studentFilteredLessons.map(l => l.studentId))]
        });
        // Fetch all tasks for the teacher
        const allTasks = await getTeacherTasks(currentUser.uid);
        console.log('[REPORTS DEBUG] Raw tasks count:', allTasks.length);
        console.log('[REPORTS DEBUG] Sample tasks (first 3):', allTasks.slice(0, 3).map(t => ({
          id: t.id,
          studentId: t.studentId,
          studentName: t.studentName,
          status: t.status,
          dueDate: t.dueDate
        })));
        // Filter tasks by date range (using dueDate)
        console.log('🔴🔴🔴 ABOUT TO CALL filterTasksByDateRange 🔴🔴🔴');
        console.log('🔴 Input tasks:', allTasks.length);
        console.log('🔴 Date range:', dateRange.startDate, 'to', dateRange.endDate);
        const filteredTasks = filterTasksByDateRange(allTasks, dateRange.startDate, dateRange.endDate);
        console.log('🔴🔴🔴 RETURNED FROM filterTasksByDateRange 🔴🔴🔴');
        console.log('🔴 Filtered tasks:', filteredTasks.length);
        console.log('[REPORTS DEBUG] After date filtering tasks:', {
          original: allTasks.length,
          filtered: filteredTasks.length
        });
        // Apply student filter to tasks if a specific student is selected
        const studentFilteredTasks = selectedStudentFilter === 'all' ? filteredTasks : filteredTasks.filter(task => {
          const matches = task.studentId === selectedStudentFilter;
          if (!matches) {
            console.log('[REPORTS DEBUG] Task filtered out:', {
              taskStudentId: task.studentId,
              selectedStudentFilter,
              match: matches
            });
          }
          return matches;
        });
        console.log('[REPORTS DEBUG] After student filtering tasks:', {
          beforeFilter: filteredTasks.length,
          afterFilter: studentFilteredTasks.length,
          selectedStudent: selectedStudentFilter,
          uniqueStudentIds: [...new Set(studentFilteredTasks.map(t => t.studentId))]
        });
        // Calculate real metrics with filtered data
        const metrics = calculateReportsMetrics(studentFilteredLessons, studentFilteredTasks);
        console.log('[REPORTS DEBUG] Calculated metrics:', {
          activeStudents: metrics.activeStudents,
          lessonCompletionRate: metrics.lessonCompletionRate,
          noShowRate: metrics.noShowRate,
          noShowCount: metrics.noShowCount,
          cancellationRate: metrics.cancellationRate,
          taskCompletionRate: metrics.taskCompletionRate,
          totalLessons: metrics.totalLessons,
          completedLessons: metrics.completedLessons,
          noShowLessons: metrics.noShowLessons,
          pastCancelledLessons: metrics.pastCancelledLessons,
          upcomingCancelledLessons: metrics.upcomingCancelledLessons,
          completedTasks: metrics.completedTasks,
          totalTasks: metrics.totalTasks
        });
        console.log('═══════════════════════════════════════════════════════');
        setKpiMetrics({
          activeStudents: metrics.activeStudents,
          lessonCompletionRate: metrics.lessonCompletionRate,
          noShowRate: metrics.noShowRate,
          noShowCount: metrics.noShowCount,
          cancellationRate: metrics.cancellationRate,
          taskCompletionRate: metrics.taskCompletionRate,
          totalLessons: metrics.totalLessons,
          completedLessons: metrics.completedLessons,
          noShowLessons: metrics.noShowLessons,
          cancelledLessons: metrics.cancelledLessons,
          pastCancelledLessons: metrics.pastCancelledLessons,
          upcomingCancelledLessons: metrics.upcomingCancelledLessons,
          completedTasks: metrics.completedTasks,
          totalTasks: metrics.totalTasks
        });
      } catch (error) {
        console.error('[REPORTS] Error calculating KPIs:', error);
        // Set to zero on error
        setKpiMetrics({
          activeStudents: 0,
          lessonCompletionRate: 0,
          noShowRate: 0,
          noShowCount: 0,
          cancellationRate: 0,
          taskCompletionRate: 0,
          totalLessons: 0,
          completedLessons: 0,
          noShowLessons: 0,
          cancelledLessons: 0,
          pastCancelledLessons: 0,
          upcomingCancelledLessons: 0,
          completedTasks: 0,
          totalTasks: 0
        });
      } finally {
        setKpisLoading(false);
      }
    };
    calculateRealKPIs();
  }, [currentUser?.uid, lessons, dateRange, selectedStudentFilter]);
  // Fetch alerts with real-time refresh
  useEffect(() => {
    const fetchAlerts = async () => {
      if (!currentUser?.uid) {
        setAlertsLoading(false);
        return;
      }
      try {
        setAlertsLoading(true);
        const allAlerts = await getAllAlerts(currentUser.uid);
        setAlerts(allAlerts);
      } catch (error) {
        console.error('Error fetching alerts:', error);
        setAlerts([]);
      } finally {
        setAlertsLoading(false);
      }
    };
    fetchAlerts();
  }, [currentUser?.uid]);
  // Fetch practice hours trend
  useEffect(() => {
    const fetchPracticeData = async () => {
      if (!currentUser?.uid) {
        setPracticeChartLoading(false);
        return;
      }
      try {
        setPracticeChartLoading(true);
        let groupBy: 'day' | 'week' | 'month' = 'week';
        if (chartView === 'month') groupBy = 'month';else if (chartView === 'quarter' || chartView === 'year') groupBy = 'month';
        const trendData = await getPracticeHoursTrend(currentUser.uid, dateRange.startDate, dateRange.endDate, groupBy);
        setPracticeData(trendData);
      } catch (error) {
        console.error('Error fetching practice data:', error);
        setPracticeData([]);
      } finally {
        setPracticeChartLoading(false);
      }
    };
    fetchPracticeData();
  }, [currentUser?.uid, dateRange, chartView]);
  // Fetch students and calculate metrics
  useEffect(() => {
    const fetchStudentsAndMetrics = async () => {
      if (!currentUser?.uid) {
        setStudentsLoading(false);
        return;
      }
      try {
        setStudentsLoading(true);
        const teacherStudents = await getTeacherStudents(currentUser.uid);
        const tasks = await getTeacherTasks(currentUser.uid);
        const now = new Date();
        const sevenDaysAgo = new Date(now);
        sevenDaysAgo.setDate(now.getDate() - 7);
        const startDateStr = sevenDaysAgo.toISOString().split('T')[0];
        const endDateStr = now.toISOString().split('T')[0];
        const fourteenDaysAgo = new Date(now);
        fourteenDaysAgo.setDate(now.getDate() - 14);
        const prevStartDateStr = fourteenDaysAgo.toISOString().split('T')[0];
        const prevEndDateStr = sevenDaysAgo.toISOString().split('T')[0];
        const studentsWithMetrics = await Promise.all(teacherStudents.map(async student => {
          const studentLessons = lessons.filter(lesson => lesson.studentId === student.studentId || lesson.studentId === student.id);
          const recentLessons = studentLessons.filter(lesson => {
            const lessonDate = new Date(lesson.startAt);
            return lessonDate >= sevenDaysAgo && lessonDate <= now;
          });
          const totalLessons = recentLessons.length;
          const completedLessons = recentLessons.filter(lesson => lesson.status === 'COMPLETED').length;
          const attendanceValue = totalLessons > 0 ? Math.round(completedLessons / totalLessons * 100) : 0;
          const prevLessons = studentLessons.filter(lesson => {
            const lessonDate = new Date(lesson.startAt);
            return lessonDate >= fourteenDaysAgo && lessonDate < sevenDaysAgo;
          });
          const prevTotalLessons = prevLessons.length;
          const prevCompletedLessons = prevLessons.filter(lesson => lesson.status === 'COMPLETED').length;
          const prevAttendanceValue = prevTotalLessons > 0 ? Math.round(prevCompletedLessons / prevTotalLessons * 100) : attendanceValue;
          const attendanceTrend = attendanceValue - prevAttendanceValue;
          const studentTasks = tasks.filter(task => task.studentId === student.studentId || task.studentId === student.id);
          const completedTasks = studentTasks.filter(task => task.status === 'completed').length;
          const taskCompletionRate = studentTasks.length > 0 ? completedTasks / studentTasks.length * 100 : 0;
          let weeklyPracticeHours = 0;
          let prevWeeklyPracticeHours = 0;
          let practiceLevel = 'No Data';
          let practiceTrend = 0;
          try {
            const practiceLogs = await getStudentPracticeLogs(currentUser.uid, student.studentId || student.id, startDateStr, endDateStr);
            const totalMinutes = practiceLogs.reduce((sum, log) => sum + (log.duration || 0), 0);
            weeklyPracticeHours = totalMinutes / 60;
            const prevPracticeLogs = await getStudentPracticeLogs(currentUser.uid, student.studentId || student.id, prevStartDateStr, prevEndDateStr);
            const prevTotalMinutes = prevPracticeLogs.reduce((sum, log) => sum + (log.duration || 0), 0);
            prevWeeklyPracticeHours = prevTotalMinutes / 60;
            practiceTrend = weeklyPracticeHours - prevWeeklyPracticeHours;
            const recommendedHours = student.recommendedPracticeHours || 5;
            const percentage = weeklyPracticeHours / recommendedHours * 100;
            if (percentage >= 90) {
              practiceLevel = 'Excellent';
            } else if (percentage >= 70) {
              practiceLevel = 'Good';
            } else if (percentage >= 50) {
              practiceLevel = 'Fair';
            } else if (weeklyPracticeHours > 0) {
              practiceLevel = 'Needs Attention';
            } else {
              practiceLevel = 'No Data';
            }
          } catch (error) {
            console.error(`Error fetching practice logs for student ${student.id}:`, error);
          }
          let progressTrend = 'Steady';
          let trendDirection: 'up' | 'down' | 'neutral' = 'neutral';
          const attendanceChange = prevAttendanceValue > 0 ? (attendanceValue - prevAttendanceValue) / prevAttendanceValue * 100 : 0;
          const practiceChange = prevWeeklyPracticeHours > 0 ? (weeklyPracticeHours - prevWeeklyPracticeHours) / prevWeeklyPracticeHours * 100 : 0;
          const overallTrendScore = attendanceChange * 0.4 + practiceChange * 0.4 + (taskCompletionRate >= 80 ? 10 : -10) * 0.2;
          if (overallTrendScore >= 10) {
            progressTrend = 'Improving';
            trendDirection = 'up';
          } else if (overallTrendScore <= -10) {
            progressTrend = 'Needs Support';
            trendDirection = 'down';
          } else {
            progressTrend = 'Steady';
            trendDirection = 'neutral';
          }
          return {
            id: student.id,
            studentId: student.studentId || student.id,
            name: student.name,
            instrument: student.instruments?.[0] || student.instrument || 'Music',
            grade: student.grade,
            progressTrend,
            trendDirection,
            practiceLevel,
            weeklyPracticeHours: weeklyPracticeHours.toFixed(1),
            attendanceSummary: `${attendanceValue}%`,
            attendanceValue,
            attendanceTrend,
            practiceTrend: practiceTrend.toFixed(1),
            taskCompletionRate: taskCompletionRate.toFixed(0)
          };
        }));
        const improving = studentsWithMetrics.filter(s => s.progressTrend === 'Improving').length;
        // P0 FIX: Calculate "At Risk" count using SAME logic as tab filter
        // Only check: attendanceValue < 75 OR practiceLevel === 'Needs Attention' OR 'Fair'
        // DO NOT include progressTrend === 'Needs Support' (that was causing mismatch)
        const needsAttention = studentsWithMetrics.filter(s => s.attendanceValue < 75 || s.practiceLevel === 'Needs Attention' || s.practiceLevel === 'Fair').length;
        // P0 FIX: Calculate "High Performers" count using SAME logic as tab filter
        const highPerformers = studentsWithMetrics.filter(s => s.attendanceValue >= 90 && (s.practiceLevel === 'Excellent' || s.practiceLevel === 'Good')).length;
        const avgAttendance = studentsWithMetrics.length > 0 ? Math.round(studentsWithMetrics.reduce((sum, s) => sum + s.attendanceValue, 0) / studentsWithMetrics.length) : 0;
        setSummaryMetrics({
          improving,
          needsAttention,
          highPerformers,
          averageAttendance: avgAttendance
        });
        setStudents(studentsWithMetrics);
      } catch (error) {
        console.error('Error fetching students:', error);
        setStudents([]);
      } finally {
        setStudentsLoading(false);
      }
    };
    fetchStudentsAndMetrics();
  }, [currentUser?.uid, lessons, dateRange]);
  // Extract lesson notes from lessons
  useEffect(() => {
    const extractLessonNotes = () => {
      try {
        setNotesLoading(true);
        const lessonsWithNotes = lessons.filter(lesson => lesson.notes && lesson.notes.trim() !== '').map(lesson => ({
          id: lesson.id,
          studentName: lesson.studentName || 'Unknown Student',
          studentId: lesson.studentId || '',
          date: new Date(lesson.startAt).toISOString().split('T')[0],
          instrument: lesson.instrument || 'Music',
          notes: lesson.notes || ''
        })).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        setLessonNotes(lessonsWithNotes);
      } catch (error) {
        console.error('Error extracting lesson notes:', error);
        setLessonNotes([]);
      } finally {
        setNotesLoading(false);
      }
    };
    extractLessonNotes();
  }, [lessons]);
  // Toggle sidebar on mobile
  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };
  // Open sidebar by default on larger screens
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsSidebarOpen(true);
      } else {
        setIsSidebarOpen(false);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  // Get chart data based on view and comparison
  const chartData = useMemo(() => {
    if (showComparison) {
      return practiceData.map(item => ({
        period: item.period,
        current: item.hours,
        previous: item.hours * 0.9
      }));
    }
    return practiceData.map(item => ({
      period: item.period,
      hours: item.hours
    }));
  }, [practiceData, showComparison]);
  // Helper function to get progress trend badge class
  const getProgressTrendClass = trend => {
    switch (trend) {
      case 'Improving':
        return isDark ? 'bg-green-500/20 text-green-500 border border-green-500/30' : 'bg-green-500/10 text-green-500 border border-green-500/30';
      case 'Steady':
        return isDark ? 'bg-blue-900/30 text-blue-200' : 'bg-blue-100 text-blue-800';
      case 'Needs Support':
        return isDark ? 'bg-yellow-900/30 text-yellow-200' : 'bg-yellow-100 text-yellow-800';
      default:
        return isDark ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-800';
    }
  };
  // Handle student card click - open panel instead of navigating
  const handleStudentClick = (alert: any) => {
    setSelectedAlert(alert);
    setIsAlertPanelOpen(true);
  };
  // Handle dismiss alert with optimistic UI update and real-time refresh
  const handleDismissAlert = async (alertId: string) => {
    if (!currentUser?.uid) return;
    try {
      await dismissAlert(currentUser.uid, alertId);
      // Refresh alerts immediately to show in dismissed tab
      const allAlerts = await getAllAlerts(currentUser.uid);
      setAlerts(allAlerts);
      toast.success('Alert dismissed successfully');
      // Dispatch event to notify Dashboard
      window.dispatchEvent(new CustomEvent('alertDismissed'));
    } catch (error) {
      console.error('Error dismissing alert:', error);
      toast.error('Failed to dismiss alert. Please try again.');
    }
  };
  // Handle message student
  const handleMessageStudent = (eOrStudentId: any, studentId?: string) => {
    if (eOrStudentId?.stopPropagation) {
      eOrStudentId.stopPropagation();
      navigate(`/teacher-messages?studentId=${studentId}`);
    } else {
      navigate(`/teacher-messages?studentId=${eOrStudentId}`);
      if (isAlertPanelOpen) {
        setIsAlertPanelOpen(false);
      }
    }
  };
  // Handle create new student from alert modal
  const handleCreateNewStudent = () => {
    setIsAddAlertModalOpen(false);
    setIsAddStudentModalOpen(true);
  };
  // Handle student created - refresh and reopen alert modal
  const handleStudentCreated = async () => {
    setIsAddStudentModalOpen(false);
    if (currentUser?.uid) {
      try {
        const teacherStudents = await getTeacherStudents(currentUser.uid);
        const tasks = await getTeacherTasks(currentUser.uid);
        const now = new Date();
        const sevenDaysAgo = new Date(now);
        sevenDaysAgo.setDate(now.getDate() - 7);
        const startDateStr = sevenDaysAgo.toISOString().split('T')[0];
        const endDateStr = now.toISOString().split('T')[0];
        const studentsWithMetrics = await Promise.all(teacherStudents.map(async student => {
          const studentLessons = lessons.filter(lesson => lesson.studentId === student.studentId || lesson.studentId === student.id);
          const totalLessons = studentLessons.length;
          const completedLessons = studentLessons.filter(lesson => lesson.status === 'COMPLETED').length;
          const attendanceValue = totalLessons > 0 ? Math.round(completedLessons / totalLessons * 100) : 0;
          const studentTasks = tasks.filter(task => task.studentId === student.studentId || task.studentId === student.id);
          const completedTasks = studentTasks.filter(task => task.status === 'completed').length;
          let weeklyPracticeHours = 0;
          let practiceLevel = 'No Data';
          try {
            const practiceLogs = await getStudentPracticeLogs(currentUser.uid, student.studentId || student.id, startDateStr, endDateStr);
            const totalMinutes = practiceLogs.reduce((sum, log) => sum + (log.duration || 0), 0);
            weeklyPracticeHours = totalMinutes / 60;
            const recommendedHours = student.recommendedPracticeHours || 5;
            const percentage = weeklyPracticeHours / recommendedHours * 100;
            if (percentage >= 90) {
              practiceLevel = 'Excellent';
            } else if (percentage >= 70) {
              practiceLevel = 'Good';
            } else if (percentage >= 50) {
              practiceLevel = 'Fair';
            } else if (weeklyPracticeHours > 0) {
              practiceLevel = 'Needs Attention';
            } else {
              practiceLevel = 'No Data';
            }
          } catch (error) {
            console.error(`Error fetching practice logs for student ${student.id}:`, error);
          }
          let progressTrend = 'Steady';
          if (attendanceValue >= 90 && completedTasks >= studentTasks.length * 0.8 && (practiceLevel === 'Excellent' || practiceLevel === 'Good')) {
            progressTrend = 'Improving';
          } else if (attendanceValue < 75 || completedTasks < studentTasks.length * 0.5 || practiceLevel === 'Needs Attention') {
            progressTrend = 'Needs Support';
          }
          return {
            id: student.id,
            studentId: student.studentId || student.id,
            name: student.name,
            instrument: student.instruments?.[0] || student.instrument || 'Music',
            progressTrend,
            practiceLevel,
            weeklyPracticeHours: weeklyPracticeHours.toFixed(1),
            attendanceSummary: `${attendanceValue}%`,
            attendanceValue
          };
        }));
        setStudents(studentsWithMetrics);
      } catch (error) {
        console.error('Error refreshing students:', error);
      }
    }
    setTimeout(() => {
      setIsAddAlertModalOpen(true);
    }, 300);
  };
  // Panel state for student alerts
  const [selectedAlert, setSelectedAlert] = useState<any>(null);
  const [isAlertPanelOpen, setIsAlertPanelOpen] = useState(false);
  // Panel state for student progress
  const [selectedStudent, setSelectedStudent] = useState<any>(null);
  const [isProgressPanelOpen, setIsProgressPanelOpen] = useState(false);
  // Add Student Modal state
  const [isAddStudentModalOpen, setIsAddStudentModalOpen] = useState(false);
  // Expand/collapse state for alerts
  const [alertsExpanded, setAlertsExpanded] = useState(false);
  const [alertsDisplayCount, setAlertsDisplayCount] = useState(3);
  // Handle add alert with real-time refresh
  const handleAddAlert = async alertData => {
    if (!currentUser?.uid) {
      throw new Error('User not authenticated');
    }
    try {
      await createAlert(currentUser.uid, {
        studentId: alertData.studentId,
        studentName: alertData.studentName,
        reason: alertData.reason,
        practiceHours: alertData.practiceHours,
        details: alertData.details
      });
      // Refresh alerts immediately
      const allAlerts = await getAllAlerts(currentUser.uid);
      setAlerts(allAlerts);
      // Dispatch event to notify Dashboard
      window.dispatchEvent(new CustomEvent('alertCreated'));
    } catch (error) {
      console.error('Error in handleAddAlert:', error);
      throw error;
    }
  };
  // Handle alert updated with real-time refresh
  const handleAlertUpdated = async () => {
    if (!currentUser?.uid) return;
    try {
      const allAlerts = await getAllAlerts(currentUser.uid);
      setAlerts(allAlerts);
      // Update selected alert if panel is open
      if (selectedAlert && isAlertPanelOpen) {
        const updatedAlert = allAlerts.find(a => a.id === selectedAlert.id);
        if (updatedAlert) {
          setSelectedAlert(updatedAlert);
        }
      }
      // Dispatch event to notify Dashboard
      window.dispatchEvent(new CustomEvent('alertUpdated'));
    } catch (error) {
      console.error('Error refreshing alerts:', error);
    }
  };
  // Handle alert deleted with real-time refresh
  const handleAlertDeleted = async (alertId: string) => {
    if (!currentUser?.uid) return;
    try {
      setAlerts(alerts.filter(alert => alert.id !== alertId));
      toast.success('Alert deleted permanently');
      // Dispatch event to notify Dashboard
      window.dispatchEvent(new CustomEvent('alertDeleted'));
    } catch (error) {
      console.error('Error handling alert deletion:', error);
      const allAlerts = await getAllAlerts(currentUser.uid);
      setAlerts(allAlerts);
    }
  };
  // Handle table sorting
  const handleSort = column => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };
  // Get sort icon
  const getSortIcon = column => {
    if (sortColumn !== column) {
      return <ChevronsUpDownIcon className="h-4 w-4 ml-1 opacity-50" data-id="element-5815" />;
    }
    return sortDirection === 'asc' ? <ArrowUpIcon className="h-4 w-4 ml-1" data-id="element-5816" /> : <ArrowDownIcon className="h-4 w-4 ml-1" data-id="element-5817" />;
  };
  // Filter and sort students
  const filteredAndSortedStudents = useMemo(() => {
    let filtered = [...students];
    if (searchQuery) {
      filtered = filtered.filter(student => student.name.toLowerCase().includes(searchQuery.toLowerCase()));
    }
    if (sortColumn) {
      filtered.sort((a, b) => {
        let aValue, bValue;
        switch (sortColumn) {
          case 'name':
            aValue = a.name;
            bValue = b.name;
            break;
          case 'progress':
            aValue = a.progressTrend;
            bValue = b.progressTrend;
            break;
          case 'practice':
            aValue = a.practiceLevel;
            bValue = a.practiceLevel;
            break;
          case 'attendance':
            aValue = a.attendanceValue;
            bValue = a.attendanceValue;
            break;
          default:
            return 0;
        }
        if (typeof aValue === 'string') {
          return sortDirection === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
        } else {
          return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
        }
      });
    }
    return filtered;
  }, [students, searchQuery, sortColumn, sortDirection]);
  // Filter lesson notes
  const filteredLessonNotes = useMemo(() => {
    let filtered = [...lessonNotes];
    if (notesSearchQuery) {
      filtered = filtered.filter(note => note.studentName.toLowerCase().includes(notesSearchQuery.toLowerCase()) || note.notes.toLowerCase().includes(notesSearchQuery.toLowerCase()));
    }
    if (notesFilterStudent !== 'all') {
      filtered = filtered.filter(note => note.studentId === notesFilterStudent);
    }
    if (notesFilterInstrument !== 'all') {
      filtered = filtered.filter(note => note.instrument === notesFilterInstrument);
    }
    return filtered;
  }, [lessonNotes, notesSearchQuery, notesFilterStudent, notesFilterInstrument]);
  // Get displayed notes (with pagination)
  const displayedNotes = filteredLessonNotes.slice(0, notesDisplayCount);
  const hasMoreNotes = filteredLessonNotes.length > notesDisplayCount;
  // Handle row click
  const handleRowClick = student => {
    setSelectedStudent(student);
    setIsProgressPanelOpen(true);
  };
  // Get unique instruments
  const instruments = useMemo(() => {
    return [...new Set(students.map(s => s.instrument))];
  }, [students]);
  const notesInstruments = useMemo(() => {
    return [...new Set(lessonNotes.map(n => n.instrument))];
  }, [lessonNotes]);
  const notesStudents = useMemo(() => {
    const uniqueStudents = new Map();
    lessonNotes.forEach(note => {
      if (!uniqueStudents.has(note.studentId)) {
        uniqueStudents.set(note.studentId, {
          id: note.studentId,
          name: note.studentName
        });
      }
    });
    return Array.from(uniqueStudents.values());
  }, [lessonNotes]);
  // Get students list for modal (from real students)
  const studentsList = useMemo(() => {
    return students.map(student => ({
      id: student.studentId || student.id,
      name: student.name
    }));
  }, [students]);
  // Get date range label - UPDATED: Added 'activenow' case
  const getDateRangeLabel = () => {
    switch (dateRangePreset) {
      case 'last7days':
        return 'Last 7 Days';
      case 'last30days':
        return 'Last 30 Days';
      case 'last90days':
        return 'Last Quarter';
      case 'activenow':
        return 'Active Now';
      case 'custom':
        if (customStartDate && customEndDate) {
          return `${customStartDate} to ${customEndDate}`;
        }
        return 'Custom Range';
      default:
        return 'Last Quarter';
    }
  };
  // Convert alerts to students needing attention format
  const studentsNeedingAttention = useMemo(() => {
    const filteredAlerts = alerts.filter(alert => {
      // Filter by alert status (active/dismissed)
      const matchesStatus = alert.status === alertFilter || !alert.status && alertFilter === 'active';
      // Filter by selected student if not "all"
      const matchesStudent = selectedStudentFilter === 'all' || alert.studentId === selectedStudentFilter;
      return matchesStatus && matchesStudent;
    });
    return filteredAlerts.map(alert => ({
      id: alert.id,
      studentId: alert.studentId,
      name: alert.studentName,
      reason: alert.reason,
      practiceHours: alert.practiceHours || 0,
      details: alert.details,
      status: alert.status || 'active'
    }));
  }, [alerts, alertFilter, selectedStudentFilter]);
  // Get displayed alerts with expand/collapse
  const displayedAlerts = alertsExpanded ? studentsNeedingAttention : studentsNeedingAttention.slice(0, alertsDisplayCount);
  return <div className={`w-full min-h-screen ${isDark ? 'bg-gradient-to-r from-[#0F1626] via-[#1E3A8A] to-[#2C3E50] text-white' : 'bg-gradient-to-r from-gray-50 to-gray-100 text-gray-800'} font-sans transition-colors duration-300`} data-id="element-5818">
      <Navbar data-id="element-5819" />
      <div className="pt-20 pb-16 flex flex-col lg:flex-row" data-id="element-5820">
        {/* Mobile sidebar toggle */}
        <button onClick={toggleSidebar} className="lg:hidden fixed bottom-4 right-4 z-30 bg-deep-pink text-white p-3 rounded-full shadow-lg flex items-center justify-center" aria-label={isSidebarOpen ? 'Close menu' : 'Open menu'} style={{
        minWidth: '56px',
        minHeight: '56px'
      }} data-id="element-5821">
          {isSidebarOpen ? <XIcon className="h-6 w-6" data-id="element-5822" /> : <MenuIcon className="h-6 w-6" data-id="element-5823" />}
        </button>

        {/* Left Sidebar */}
        <aside className={`w-64 fixed inset-y-0 pt-20 pb-16 ${isDark ? 'bg-navy-800 shadow-lg' : 'bg-white shadow-md'} z-20 transform transition-all duration-300 ease-in-out ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`} data-id="element-5824">
          <TeacherSidebar darkMode={isDark} closeSidebar={() => setIsSidebarOpen(false)} data-id="element-5825" />
        </aside>

        {/* Main Content */}
        <main className={`flex-1 transition-all duration-300 ease-in-out ${isSidebarOpen ? 'lg:ml-64' : 'ml-0 lg:ml-64'} px-4 py-6`} data-id="element-5826">
          <div className="mx-auto" data-id="element-5827">
            {/* Header with Add Alert Button */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6" data-id="element-5828">
              <h1 className={`text-2xl sm:text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-800'} mb-4 sm:mb-0`} data-id="element-5829">
                Reports
              </h1>
              <button onClick={() => setIsAddAlertModalOpen(true)} className={`px-4 py-2 rounded-md border-2 transition-colors flex items-center justify-center w-full sm:w-auto ${isDark ? 'border-[#10B981] text-[#10B981] hover:bg-[#10B981]/10' : 'border-[#10B981] text-[#10B981] hover:bg-[#10B981]/5'}`} style={{
              minWidth: '140px'
            }} data-id="element-5830">
                Add Alert
              </button>
            </div>

            {/* REMOVED: Development disclaimer - Reports are now production-ready */}

            {/* Students Needing Attention - UPDATED */}
            {alertsLoading ? <div className={`rounded-xl ${isDark ? 'bg-red-500/10 border-2 border-red-500/30' : 'bg-red-50 border-2 border-red-200'} p-6 mb-6`} data-id="element-5831">
                <div className="flex items-center mb-4" data-id="element-5832">
                  <SkeletonLoader className="h-6 w-6 rounded mr-3" darkMode={isDark} data-id="element-5833" />
                  <SkeletonLoader className="h-6 w-64" darkMode={isDark} data-id="element-5834" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-5835">
                  {[1, 2, 3].map(i => <div key={i} className={`p-4 rounded-lg ${isDark ? 'bg-navy-800' : 'bg-white'}`} data-id="element-5836">
                      <SkeletonLoader className="h-5 w-32 mb-2" darkMode={isDark} data-id="element-5837" />
                      <SkeletonLoader className="h-4 w-24 mb-2" darkMode={isDark} data-id="element-5838" />
                      <SkeletonLoader className="h-4 w-28" darkMode={isDark} data-id="element-5839" />
                    </div>)}
                </div>
              </div> : studentsNeedingAttention.length > 0 ? <div className={`rounded-xl ${isDark ? 'bg-red-500/10 border-2 border-red-500/30' : 'bg-red-50 border-2 border-red-200'} p-6 mb-6`} data-id="element-5840">
                <div className="flex items-center justify-between mb-4" data-id="element-5841">
                  <div className="flex items-center" data-id="element-5842">
                    <AlertCircleIcon className="h-6 w-6 text-red-500 mr-3" data-id="element-5843" />
                    <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-5844">
                      Students Needing Attention
                    </h2>
                  </div>

                  {/* Alert Filter Toggle */}
                  <div className={`flex items-center gap-1 p-1 rounded-lg ${isDark ? 'bg-navy-800' : 'bg-white'}`} data-id="element-5845">
                    <button onClick={() => setAlertFilter('active')} className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${alertFilter === 'active' ? 'text-[#10B981] border-2 border-[#10B981] bg-transparent' : isDark ? 'text-gray-400 hover:text-white border-2 border-transparent' : 'text-gray-600 hover:text-gray-900 border-2 border-transparent'}`} data-id="element-5846">
                      Active
                    </button>
                    <button onClick={() => setAlertFilter('dismissed')} className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${alertFilter === 'dismissed' ? 'text-[#EF4444] border-2 border-[#EF4444] bg-transparent' : isDark ? 'text-gray-400 hover:text-white border-2 border-transparent' : 'text-gray-600 hover:text-gray-900 border-2 border-transparent'}`} data-id="element-5847">
                      Dismissed
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-5848">
                  {displayedAlerts.map(student => {
                // Truncate reason to ~50 characters
                const reasonPreview = student.reason.length > 50 ? student.reason.substring(0, 50) + '...' : student.reason;
                // Truncate details to ~60 characters if exists
                const detailsPreview = student.details && student.details.length > 60 ? student.details.substring(0, 60) + '...' : student.details;
                const hasMore = student.reason.length > 50 || student.details && student.details.length > 60;
                return <div key={student.id} onClick={() => handleStudentClick(student)} className={`p-4 rounded-lg cursor-pointer transition-all duration-200 hover:shadow-md hover:-translate-y-1 ${isDark ? 'bg-navy-800 hover:bg-navy-700' : 'bg-white hover:bg-gray-50'}`} data-id="element-5849">
                        <h3 className={`font-medium mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-5850">
                          {student.name}
                        </h3>
                        <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-5851">
                          {reasonPreview}
                        </p>
                        {detailsPreview && <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-5852">
                            {detailsPreview}
                          </p>}
                        {student.practiceHours !== undefined && <p className={`text-sm mt-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-5853">
                            Practice: {student.practiceHours} hrs/week
                          </p>}
                        {hasMore && <p className={`text-sm mt-2 font-medium ${isDark ? 'text-red-400' : 'text-red-600'}`} data-id="element-5854">
                            View more
                          </p>}
                      </div>;
              })}
                </div>

                {/* Expand/Collapse Button */}
                {studentsNeedingAttention.length > alertsDisplayCount && <div className="mt-4 text-center" data-id="element-5855">
                    <button onClick={() => setAlertsExpanded(!alertsExpanded)} className="text-red-500 hover:text-red-600 text-sm font-medium flex items-center justify-center mx-auto transition-colors" data-id="element-5856">
                      {alertsExpanded ? <>
                          Show Less
                          <ChevronRightIcon className="h-4 w-4 ml-1 rotate-90" data-id="element-5857" />
                        </> : <>
                          View All ({studentsNeedingAttention.length})
                          <ChevronRightIcon className="h-4 w-4 ml-1" data-id="element-5858" />
                        </>}
                    </button>
                  </div>}
              </div> : <div className={`rounded-xl ${isDark ? 'bg-red-500/10 border-2 border-red-500/30' : 'bg-red-50 border-2 border-red-200'} p-6 mb-6`} data-id="element-5859">
                <div className="flex items-center justify-between mb-4" data-id="element-5860">
                  <div className="flex items-center flex-1" data-id="element-5861">
                    <AlertCircleIcon className="h-6 w-6 text-red-500 mr-3" data-id="element-5862" />
                    <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-5863">
                      Students Needing Attention
                    </h2>
                  </div>

                  {/* Alert Filter Toggle */}
                  <div className={`flex items-center gap-1 p-1 rounded-lg ${isDark ? 'bg-navy-800' : 'bg-white'}`} data-id="element-5864">
                    <button onClick={() => setAlertFilter('active')} className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${alertFilter === 'active' ? 'text-[#10B981] border-2 border-[#10B981] bg-transparent' : isDark ? 'text-gray-400 hover:text-white border-2 border-transparent' : 'text-gray-600 hover:text-gray-900 border-2 border-transparent'}`} data-id="element-5865">
                      Active
                    </button>
                    <button onClick={() => setAlertFilter('dismissed')} className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${alertFilter === 'dismissed' ? 'text-[#EF4444] border-2 border-[#EF4444] bg-transparent' : isDark ? 'text-gray-400 hover:text-white border-2 border-transparent' : 'text-gray-600 hover:text-gray-900 border-2 border-transparent'}`} data-id="element-5866">
                      Dismissed
                    </button>
                  </div>
                </div>

                <div className={`p-4 rounded-lg ${isDark ? 'bg-navy-800' : 'bg-white'}`} data-id="element-5867">
                  <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-5868">
                    {alertFilter === 'active' ? 'No students need attention right now' : 'No dismissed alerts'}
                  </p>
                  <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-5869">
                    {alertFilter === 'active' ? 'All students are on track.' : 'Dismissed alerts will appear here'}
                  </p>
                </div>
              </div>}

            {/* Date Range Selector */}
            <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-5870">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4 flex-1" data-id="element-5871">
                {/* HIDDEN: Date range label - creates visual clutter
                 <p
                 className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
                 >
                 {getDateRangeLabel()}
                 </p>
                 */}

                {/* Student Filter Dropdown */}
                <select value={selectedStudentFilter} onChange={e => {
                console.log('[REPORTS DEBUG] Student filter changed:', {
                  from: selectedStudentFilter,
                  to: e.target.value
                });
                setSelectedStudentFilter(e.target.value);
              }} className={`px-4 py-2 rounded-md font-medium transition-colors border-2 appearance-none cursor-pointer ${isDark ? 'border-[#3B82F6] text-[#3B82F6] bg-transparent hover:bg-[#3B82F6]/10' : 'border-[#3B82F6] text-[#3B82F6] bg-transparent hover:bg-[#3B82F6]/5'} focus:outline-none focus:ring-0`} style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%233B82F6' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 0.75rem center',
                paddingRight: '2.5rem'
              }} data-id="element-5872">
                  <option value="all" data-id="element-5873">All Students</option>
                  {students.map(student => <option key={student.id} value={student.studentId} data-id="element-5874">
                      {student.name}
                    </option>)}
                </select>

                {/* Debug Button - Only visible in development */}
                {process.env.NODE_ENV === 'development' && <button onClick={() => {
                console.log('═══════════════════════════════════════════════════════');
                console.log('[REPORTS DEBUG DUMP] Current State:');
                console.log('Selected Student Filter:', selectedStudentFilter);
                console.log('Date Range:', dateRange);
                console.log('Students:', students.map(s => ({
                  id: s.id,
                  studentId: s.studentId,
                  name: s.name
                })));
                console.log('Lessons:', lessons.map(l => ({
                  id: l.id,
                  studentId: l.studentId,
                  studentName: l.studentName
                })));
                console.log('KPI Metrics:', kpiMetrics);
                console.log('═══════════════════════════════════════════════════════');
              }} className={`px-3 py-2 rounded-md text-xs font-medium border ${isDark ? 'border-yellow-500 text-yellow-500 hover:bg-yellow-500/10' : 'border-yellow-600 text-yellow-600 hover:bg-yellow-600/5'}`} data-id="element-5875">
                    Debug State
                  </button>}
              </div>

              <select value={dateRangePreset} onChange={e => setDateRangePreset(e.target.value)} className={`px-4 py-2 rounded-md font-medium transition-colors border-2 appearance-none cursor-pointer ${isDark ? 'border-[#10B981] text-[#10B981] bg-transparent hover:bg-[#10B981]/10' : 'border-[#10B981] text-[#10B981] bg-transparent hover:bg-[#10B981]/5'} focus:outline-none focus:ring-0`} style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%2310B981' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 0.75rem center',
              paddingRight: '2.5rem'
            }} data-id="element-5876">
                <option value="last7days" data-id="element-5877">Last Week</option>
                <option value="last30days" data-id="element-5878">Last Month</option>
                <option value="last90days" data-id="element-5879">Last Quarter</option>
                <option value="activenow" data-id="element-5880">Active Now</option>
                <option value="custom" data-id="element-5881">Custom</option>
              </select>
            </div>

            {/* Custom Date Range Inputs */}
            {dateRangePreset === 'custom' && <div className={`mb-6 rounded-xl ${isDark ? 'bg-navy-800' : 'bg-white'} shadow-sm`} data-id="element-5882">
                <div className="p-4" data-id="element-5883">
                  <div className="flex flex-col sm:flex-row gap-4 sm:max-w-2xl" data-id="element-5884">
                    <div className="flex-1 min-w-0" data-id="element-5885">
                      <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-5886">
                        Start Date
                      </label>
                      <input type="date" value={customStartDate} onChange={e => setCustomStartDate(e.target.value)} className={`w-full px-3 py-2 rounded-lg ${isDark ? 'bg-navy-900/50 text-white border-navy-700' : 'bg-gray-50 text-gray-900 border-gray-300'} border focus:ring-2 focus:ring-deep-pink focus:border-transparent`} data-id="element-5887" />
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-5888">
                      <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-5889">
                        End Date
                      </label>
                      <input type="date" value={customEndDate} onChange={e => setCustomEndDate(e.target.value)} className={`w-full px-3 py-2 rounded-lg ${isDark ? 'bg-navy-900/50 text-white border-navy-700' : 'bg-gray-50 text-gray-900 border-gray-300'} border focus:ring-2 focus:ring-deep-pink focus:border-transparent`} data-id="element-5890" />
                    </div>
                  </div>
                </div>
              </div>}

            {/* HIDDEN: Attendance Summary Stats - Not ready yet */}
            {SHOW_KPI_CARDS && <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6" data-id="element-5891">
                <StatCard icon={<UserIcon className="h-6 w-6 text-[#3B82F6]" data-id="element-5893" />} label="Active Students" value={kpiMetrics.activeStudents.toString()} darkMode={isDark} isLoading={kpisLoading} onClick={() => {}} data-id="element-5892" />
                <StatCard icon={<CheckCircleIcon className="h-6 w-6 text-[#10B981]" data-id="element-5895" />} label="Lesson Completion" value={`${kpiMetrics.lessonCompletionRate}%`} darkMode={isDark} isLoading={kpisLoading} onClick={() => {}} data-id="element-5894" />
                <StatCard icon={<UserXIcon className="h-6 w-6 text-[#F59E0B]" data-id="element-5897" />} label="No-Shows" value={`${kpiMetrics.noShowCount} (${kpiMetrics.noShowRate}%)`} darkMode={isDark} isLoading={kpisLoading} onClick={() => {}} data-id="element-5896" />
                {/* Split Cancelled Lessons Card */}
                <div className={`p-6 rounded-xl ${isDark ? 'bg-navy-800' : 'bg-white'} shadow-sm cursor-pointer transition-all duration-200 hover:shadow-md hover:-translate-y-1`} onClick={() => {}} data-id="element-5898">
                  {kpisLoading ? <div className="flex justify-between items-start" data-id="element-5899">
                      <div className="flex-1" data-id="element-5900">
                        <SkeletonLoader className="h-4 w-24 mb-2" darkMode={isDark} data-id="element-5901" />
                        <SkeletonLoader className="h-8 w-16" darkMode={isDark} data-id="element-5902" />
                      </div>
                      <SkeletonLoader className="h-12 w-12 rounded-full" darkMode={isDark} data-id="element-5903" />
                    </div> : <div className="flex justify-between items-start" data-id="element-5904">
                      <div data-id="element-5905">
                        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-5906">
                          Cancelled Lessons
                        </p>
                        <div className="mt-1 space-y-1" data-id="element-5907">
                          <div className="flex items-baseline gap-2" data-id="element-5908">
                            <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-5909">
                              Past:
                            </span>
                            <span className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-5910">
                              {kpiMetrics.pastCancelledLessons}
                            </span>
                          </div>
                          <div className="flex items-baseline gap-2" data-id="element-5911">
                            <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-5912">
                              Upcoming:
                            </span>
                            <span className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-5913">
                              {kpiMetrics.upcomingCancelledLessons}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className={`p-3 rounded-full ${isDark ? 'bg-navy-700' : 'bg-gray-100'}`} data-id="element-5914">
                        <XCircleIcon className="h-6 w-6 text-[#EF4444]" data-id="element-5915" />
                      </div>
                    </div>}
                </div>
              </div>}

            {/* Second row of KPI cards */}
            {SHOW_KPI_CARDS && <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6" data-id="element-5916">
                <StatCard icon={<ClipboardListIcon className="h-6 w-6 text-[#8B5CF6]" data-id="element-5918" />} label="Completed Tasks" value={(kpiMetrics.completedTasks ?? 0).toString()} darkMode={isDark} isLoading={kpisLoading} onClick={() => {}} data-id="element-5917" />
              </div>}

            {/* HIDDEN: Summary Cards - Not ready yet */}
            {false && <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6" data-id="element-5919">
                <div className={`p-4 rounded-xl ${isDark ? 'bg-navy-800' : 'bg-white'} shadow-sm`} data-id="element-5920">
                  <div className="flex items-center justify-between" data-id="element-5921">
                    <div data-id="element-5922">
                      <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-5923">
                        Students Improving
                      </p>
                      <h3 className={`text-2xl font-bold mt-1 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-5924">
                        {summaryMetrics.improving}
                      </h3>
                    </div>
                    <TrendingUpIcon className="h-8 w-8 text-green-500" data-id="element-5925" />
                  </div>
                </div>
                <div className={`p-4 rounded-xl ${isDark ? 'bg-navy-800' : 'bg-white'} shadow-sm`} data-id="element-5926">
                  <div className="flex items-center justify-between" data-id="element-5927">
                    <div data-id="element-5928">
                      <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-5929">
                        Average Attendance
                      </p>
                      <h3 className={`text-2xl font-bold mt-1 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-5930">
                        {summaryMetrics.averageAttendance}%
                      </h3>
                    </div>
                    <CalendarIcon className="h-8 w-8 text-blue-500" data-id="element-5931" />
                  </div>
                </div>
              </div>}

            {/* HIDDEN: Practice Hours Chart - Not ready yet */}
            {SHOW_PRACTICE_CHART && <div className={`rounded-xl ${isDark ? 'bg-navy-800' : 'bg-white'} shadow-sm p-6 mb-6`} data-id="element-5932">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-4" data-id="element-5933">
                  <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-5934">
                    Practice Hours Trend
                  </h2>

                  {/* DISABLED: Time period selector - Coming soon */}
                  <div className="relative" data-id="element-5935">
                    <select disabled value={chartView} className={`px-4 py-2 rounded-md font-medium border-2 appearance-none cursor-not-allowed opacity-50 ${isDark ? 'border-gray-600 text-gray-500 bg-navy-700' : 'border-gray-300 text-gray-400 bg-gray-100'}`} style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236B7280' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 0.75rem center',
                  paddingRight: '2.5rem'
                }} data-id="element-5936">
                      <option value="week" data-id="element-5937">Week</option>
                      <option value="month" data-id="element-5938">Month</option>
                      <option value="quarter" data-id="element-5939">Quarter</option>
                      <option value="year" data-id="element-5940">Year</option>
                    </select>
                    <span className={`absolute -bottom-6 right-0 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-5941">
                      Coming soon
                    </span>
                  </div>
                </div>

                {practiceChartLoading ? <div className="h-[300px] flex items-center justify-center" data-id="element-5942">
                    <LoaderIcon className="h-8 w-8 text-deep-pink animate-spin" data-id="element-5943" />
                  </div> : chartData.length > 0 ? <ResponsiveContainer width="100%" height={300} data-id="element-5944">
                    <AreaChart data={chartData} margin={{
                top: 10,
                right: 30,
                left: 0,
                bottom: 0
              }} data-id="element-5945">
                      <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#2D3748' : '#E2E8F0'} data-id="element-5946" />
                      <XAxis dataKey="period" tick={{
                  fill: isDark ? '#CBD5E0' : '#4A5568',
                  fontSize: 12
                }} data-id="element-5947" />
                      <YAxis tick={{
                  fill: isDark ? '#CBD5E0' : '#4A5568',
                  fontSize: 12
                }} label={{
                  value: 'Hours',
                  angle: -90,
                  position: 'insideLeft',
                  style: {
                    fill: isDark ? '#CBD5E0' : '#4A5568',
                    fontSize: 12
                  }
                }} data-id="element-5948" />
                      <Tooltip content={<CustomTooltip isDark={isDark} data-id="element-5950" />} data-id="element-5949" />
                      {showComparison && <Legend data-id="element-5951" />}
                      {showComparison ? <>
                          <Area type="monotone" dataKey="current" name="Current Period" stroke="#10B981" fill="#10B981" fillOpacity={0.6} strokeWidth={2} data-id="element-5952" />
                          <Area type="monotone" dataKey="previous" name="Previous Period" stroke="#6B7280" fill="#6B7280" fillOpacity={0.3} strokeWidth={2} strokeDasharray="5 5" data-id="element-5953" />
                        </> : <Area type="monotone" dataKey="hours" stroke="#10B981" fill="#10B981" fillOpacity={0.6} strokeWidth={2} data-id="element-5954" />}
                    </AreaChart>
                  </ResponsiveContainer> : <div className="h-[300px] flex items-center justify-center" data-id="element-5955">
                    <div className="text-center" data-id="element-5956">
                      <TrendingUpIcon className={`h-12 w-12 mx-auto mb-4 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} data-id="element-5957" />
                      <p className={`${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-5958">
                        No practice data available yet
                      </p>
                      <p className={`text-sm mt-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-5959">
                        Practice logs will appear here once students start
                        tracking
                      </p>
                    </div>
                  </div>}
              </div>}

            {/* Student Progress Overview Table */}
            {true && <div className={`rounded-xl ${isDark ? 'bg-navy-800' : 'bg-white'} shadow-sm p-6 mb-6`} data-id="element-5960">
                <h2 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-5961">
                  Student Progress Overview
                </h2>

                {/* Search Input Only */}
                <div className="mb-4" data-id="element-5962">
                  <div className={`relative flex items-center rounded-lg px-3 w-64 ${isDark ? 'bg-navy-900/50' : 'bg-white'} shadow-sm`} data-id="element-5963">
                    <SearchIcon className={`absolute left-3 h-4 w-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-5964" />
                    <input type="text" placeholder="Search students..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className={`w-full pl-10 pr-3 py-3 bg-transparent border-none focus:ring-0 focus:outline-none ${isDark ? 'text-white placeholder-gray-400' : 'text-gray-900 placeholder-gray-500'}`} data-id="element-5965" />
                  </div>
                </div>

                {studentsLoading ? <div className="space-y-3" data-id="element-5966">
                    {[1, 2, 3, 4, 5].map(i => <div key={i} className="flex items-center space-x-4" data-id="element-5967">
                        <SkeletonLoader className="h-10 w-10 rounded-full" darkMode={isDark} data-id="element-5968" />
                        <div className="flex-1" data-id="element-5969">
                          <SkeletonLoader className="h-4 w-32 mb-2" darkMode={isDark} data-id="element-5970" />
                          <SkeletonLoader className="h-3 w-24" darkMode={isDark} data-id="element-5971" />
                        </div>
                        <SkeletonLoader className="h-6 w-20" darkMode={isDark} data-id="element-5972" />
                        <SkeletonLoader className="h-4 w-16" darkMode={isDark} data-id="element-5973" />
                        <SkeletonLoader className="h-4 w-12" darkMode={isDark} data-id="element-5974" />
                      </div>)}
                  </div> : filteredAndSortedStudents.length > 0 ? <div className="overflow-x-auto" data-id="element-5975">
                    <table className={`min-w-full divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`} data-id="element-5976">
                      <thead data-id="element-5977">
                        <tr data-id="element-5978">
                          <th onClick={() => handleSort('name')} className={`px-6 py-3 text-left text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'} uppercase tracking-wider cursor-pointer hover:text-[#3B82F6] transition-colors`} data-id="element-5979">
                            <div className="flex items-center" data-id="element-5980">
                              Student
                              {getSortIcon('name')}
                            </div>
                          </th>
                          {SHOW_PROGRESS_COLUMNS && <th onClick={() => handleSort('progress')} className={`px-6 py-3 text-left text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'} uppercase tracking-wider cursor-pointer hover:text-[#3B82F6] transition-colors`} data-id="element-5981">
                              <div className="flex items-center" data-id="element-5982">
                                Progress Trend
                                {getSortIcon('progress')}
                              </div>
                            </th>}
                          {SHOW_PROGRESS_COLUMNS && <th onClick={() => handleSort('practice')} className={`px-6 py-3 text-left text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'} uppercase tracking-wider cursor-pointer hover:text-[#3B82F6] transition-colors`} data-id="element-5983">
                              <div className="flex items-center" data-id="element-5984">
                                Practice Level
                                {getSortIcon('practice')}
                              </div>
                            </th>}
                          {SHOW_PROGRESS_COLUMNS && <th className={`px-6 py-3 text-left text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'} uppercase tracking-wider`} data-id="element-5985">
                              Attendance
                            </th>}
                        </tr>
                      </thead>
                      <tbody className={`${isDark ? 'divide-y divide-gray-700' : 'divide-y divide-gray-200'}`} data-id="element-5986">
                        {filteredAndSortedStudents.map(student => <tr key={student.id} onMouseEnter={() => setHoveredRow(student.id)} onMouseLeave={() => setHoveredRow(null)} onClick={() => handleRowClick(student)} className={`cursor-pointer transition-all duration-200 ${isDark ? 'hover:bg-navy-700' : 'hover:bg-gray-50'}`} data-id="element-5987">
                            <td className="px-6 py-4 whitespace-nowrap" data-id="element-5988">
                              <div className="flex items-center" data-id="element-5989">
                                <div className={`h-10 w-10 rounded-full flex items-center justify-center mr-3 ${isDark ? 'bg-navy-700' : 'bg-gray-200'}`} data-id="element-5990">
                                  <UserIcon className="h-6 w-6 text-[#3B82F6]" data-id="element-5991" />
                                </div>
                                <div data-id="element-5992">
                                  <div className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-5993">
                                    {student.name}
                                  </div>
                                  <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-5994">
                                    {student.instrument}
                                  </div>
                                </div>
                              </div>
                            </td>
                            {SHOW_PROGRESS_COLUMNS && <td className="px-6 py-4 whitespace-nowrap" data-id="element-5995">
                                <div className="flex items-center gap-2" data-id="element-5996">
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getProgressTrendClass(student.progressTrend)}`} data-id="element-5997">
                                    {student.progressTrend}
                                  </span>
                                  {student.trendDirection === 'up' && <TrendingUpIcon className="h-4 w-4 text-green-500" data-id="element-5998" />}
                                  {student.trendDirection === 'down' && <TrendingDownIcon className="h-4 w-4 text-red-500" data-id="element-5999" />}
                                  {student.trendDirection === 'neutral' && <MinusIcon className="h-4 w-4 text-gray-400" data-id="element-6000" />}
                                </div>
                              </td>}
                            {SHOW_PROGRESS_COLUMNS && <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-6001">
                                <div data-id="element-6002">
                                  <div className="font-medium" data-id="element-6003">
                                    {student.practiceLevel}
                                  </div>
                                  {student.weeklyPracticeHours && student.practiceLevel !== 'No Data' && <div className="text-xs text-gray-500" data-id="element-6004">
                                        {student.weeklyPracticeHours} hrs/week
                                      </div>}
                                </div>
                              </td>}
                            {SHOW_PROGRESS_COLUMNS && <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-6005">
                                {student.attendanceSummary}
                              </td>}
                          </tr>)}
                      </tbody>
                    </table>
                  </div> : <div className={`py-12 text-center ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-6006">
                    <UserIcon className="h-12 w-12 mx-auto mb-4 opacity-50" data-id="element-6007" />
                    <p className="text-lg" data-id="element-6008">
                      {selectedStudentFilter !== 'all' && students.length > 0 ? 'Student not found' : 'No students found'}
                    </p>
                    <p className="mt-2" data-id="element-6009">
                      {selectedStudentFilter !== 'all' && students.length > 0 ? 'The selected student may have been removed or the filter is invalid' : searchQuery ? 'Try adjusting your search' : 'Add students to see their progress here'}
                    </p>
                    {selectedStudentFilter !== 'all' && <button onClick={() => setSelectedStudentFilter('all')} className={`mt-4 px-4 py-2 rounded-lg font-medium transition-colors ${isDark ? 'bg-navy-700 text-white hover:bg-navy-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-6010">
                        Clear Student Filter
                      </button>}
                  </div>}
              </div>}

            {/* HIDDEN: Recent Lesson Notes - Not ready yet */}
            {SHOW_LESSON_NOTES && <div className={`rounded-xl ${isDark ? 'bg-navy-800' : 'bg-white'} shadow-sm p-6`} data-id="element-6011">
                <h2 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-6012">
                  Recent Lesson Notes
                </h2>

                <div className="mb-4 grid grid-cols-1 md:grid-cols-3 gap-3" data-id="element-6013">
                  <div className={`relative flex items-center rounded-lg px-3 ${isDark ? 'bg-navy-900' : 'bg-white'} shadow-sm`} data-id="element-6014">
                    <SearchIcon className={`absolute left-3 h-4 w-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-6015" />
                    <input type="text" placeholder="Search notes..." value={notesSearchQuery} onChange={e => setNotesSearchQuery(e.target.value)} className={`w-full pl-10 pr-3 py-3 bg-transparent border-none focus:ring-0 focus:outline-none ${isDark ? 'text-white placeholder-gray-400' : 'text-gray-900 placeholder-gray-500'}`} data-id="element-6016" />
                  </div>

                  <select value={notesFilterStudent} onChange={e => setNotesFilterStudent(e.target.value)} className={`px-3 py-2 rounded-lg border-none focus:ring-0 ${isDark ? 'bg-navy-900 text-white' : 'bg-white text-gray-800'}`} data-id="element-6017">
                    <option value="all" data-id="element-6018">All Students</option>
                    {notesStudents.map(student => <option key={student.id} value={student.id} data-id="element-6019">
                        {student.name}
                      </option>)}
                  </select>

                  <select value={notesFilterInstrument} onChange={e => setNotesFilterInstrument(e.target.value)} className={`px-3 py-2 rounded-lg border-none focus:ring-0 ${isDark ? 'bg-navy-900 text-white' : 'bg-white text-gray-800'}`} data-id="element-6020">
                    <option value="all" data-id="element-6021">All Instruments</option>
                    {notesInstruments.map(instrument => <option key={instrument} value={instrument} data-id="element-6022">
                        {instrument}
                      </option>)}
                  </select>
                </div>

                {notesLoading ? <div className="space-y-4" data-id="element-6023">
                    {[1, 2, 3].map(i => <div key={i} className={`p-4 rounded-lg ${isDark ? 'bg-navy-700/50' : 'bg-gray-50'}`} data-id="element-6024">
                        <div className="flex justify-between items-start mb-2" data-id="element-6025">
                          <div className="flex-1" data-id="element-6026">
                            <SkeletonLoader className="h-5 w-32 mb-2" darkMode={isDark} data-id="element-6027" />
                            <SkeletonLoader className="h-3 w-24" darkMode={isDark} data-id="element-6028" />
                          </div>
                          <SkeletonLoader className="h-5 w-5 rounded" darkMode={isDark} data-id="element-6029" />
                        </div>
                        <SkeletonLoader className="h-4 w-full" darkMode={isDark} data-id="element-6030" />
                      </div>)}
                  </div> : displayedNotes.length > 0 ? <>
                    <div className="space-y-4" data-id="element-6031">
                      {displayedNotes.map(note => <div key={note.id} className={`p-4 rounded-lg border ${isDark ? 'bg-navy-700/50 border-navy-600' : 'bg-gray-50 border-gray-200'}`} data-id="element-6032">
                          <div className="flex justify-between items-start mb-2" data-id="element-6033">
                            <div data-id="element-6034">
                              <h3 className={`font-medium ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-6035">
                                {note.studentName}
                              </h3>
                              <div className="flex items-center gap-2 mt-1" data-id="element-6036">
                                <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-6037">
                                  {new Date(note.date).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                                </span>
                                <span className={`text-xs px-2 py-0.5 rounded-full ${isDark ? 'bg-navy-600 text-gray-300' : 'bg-gray-200 text-gray-700'}`} data-id="element-6038">
                                  {note.instrument}
                                </span>
                              </div>
                            </div>
                            <FileTextIcon className={`h-5 w-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-6039" />
                          </div>
                          <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-6040">
                            {note.notes}
                          </p>
                        </div>)}
                    </div>

                    {hasMoreNotes && <div className="mt-4 text-center" data-id="element-6041">
                        <button onClick={() => setNotesDisplayCount(notesDisplayCount + 3)} className={`px-6 py-2 rounded-lg font-medium transition-all ${isDark ? 'bg-navy-700 text-white hover:bg-navy-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-6042">
                          Load More Notes (
                          {filteredLessonNotes.length - notesDisplayCount}{' '}
                          remaining)
                        </button>
                      </div>}
                  </> : <div className={`py-12 text-center ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-6043">
                    <FileTextIcon className="h-12 w-12 mx-auto mb-4 opacity-50" data-id="element-6044" />
                    <p className="text-lg" data-id="element-6045">No lesson notes found</p>
                    <p className="mt-2" data-id="element-6046">
                      {notesSearchQuery || notesFilterStudent !== 'all' || notesFilterInstrument !== 'all' ? 'Try adjusting your filters' : 'Lesson notes will appear here after you complete lessons'}
                    </p>
                  </div>}
              </div>}
          </div>
        </main>

        {/* Overlay for mobile when sidebar is open */}
        {isSidebarOpen && <div className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-10" onClick={toggleSidebar} aria-hidden="true" data-id="element-6047"></div>}
      </div>

      {/* Attendance Detail Modal */}
      <AttendanceDetailModal isOpen={selectedModal !== null} onClose={() => setSelectedModal(null)} type={selectedModal} darkMode={isDark} data-id="element-6048" />

      {/* Add Alert Modal - Enhanced */}
      <AddAlertModal isOpen={isAddAlertModalOpen} onClose={() => setIsAddAlertModalOpen(false)} onSave={handleAddAlert} darkMode={isDark} studentsList={studentsList} onCreateStudent={handleCreateNewStudent} data-id="element-6049" />

      {/* Add Student Modal */}
      <AddStudentModal isOpen={isAddStudentModalOpen} onClose={() => setIsAddStudentModalOpen(false)} onStudentAdded={handleStudentCreated} darkMode={isDark} data-id="element-6050" />

      {/* Student Alert Panel */}
      <StudentAlertPanel open={isAlertPanelOpen} alert={selectedAlert} onClose={() => {
      setIsAlertPanelOpen(false);
      setSelectedAlert(null);
    }} onDismiss={handleDismissAlert} onMessage={handleMessageStudent} onAlertUpdated={handleAlertUpdated} onAlertDeleted={handleAlertDeleted} darkMode={isDark} data-id="element-6051" />

      {/* Student Progress Panel */}
      <StudentProgressPanel open={isProgressPanelOpen} student={selectedStudent} onClose={() => {
      setIsProgressPanelOpen(false);
      setSelectedStudent(null);
    }} onViewFullProfile={() => navigate('/my-students')} onMessage={() => navigate(`/teacher-messages?studentId=${selectedStudent?.studentId}`)} darkMode={isDark} data-id="element-6052" />

      <Footer data-id="element-6053" />
    </div>;
};